import { cn } from "@/lib/utils";
import { ErrorMessage } from "@hookform/error-message";
import {
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { FieldErrorsImpl, FieldValues } from "react-hook-form";
import { Input } from "./Input";
import { Label } from "./Label";

export interface FormItemProps<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement & HTMLTextAreaElement> {
  label?: ReactNode;
  errors?: FieldErrorsImpl<T>;
  rows?: number;
  className?: string;
  byline?: ReactNode;
}

export const FormItem = forwardRef(function FormItem<T extends FieldValues>(
  { id, label, type, errors, className, byline, ...props }: FormItemProps<T>,
  ref: ForwardedRef<HTMLInputElement & HTMLTextAreaElement>
) {
  const hasError = !!errors?.[props.name as any];

  return (
    <div className="space-y-2">
      {!!label && <Label htmlFor={id ?? props.name}>{label}</Label>}
      {type === "textarea" ? (
        <textarea
          {...props}
          id={id ?? props.name}
          className={cn(
            "w-full rounded-md border-0 bg-gray-100 px-4 py-3 text-gray-700 outline-primary read-only:outline-none disabled:opacity-50 dark:bg-gray-900 dark:text-white",
            hasError && "outline-red-400",
            className
          )}
          ref={ref}
        />
      ) : (
        <Input
          {...props}
          id={id ?? props.name}
          className={className}
          ref={ref}
          error={hasError}
        />
      )}

      {!!props.name && (
        <ErrorMessage
          errors={errors}
          name={props.name as any}
          as={<div className="mx-4 text-sm text-red-500" />}
        />
      )}

      {!!byline && <div>{byline}</div>}
    </div>
  );
});
